import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  ShoppingCart, Trash2, Plus, Minus, Package, Tag, 
  MessageCircle, Percent, ChevronRight, ArrowLeft, Loader2, Heart
} from "lucide-react";
import api from "@/utils/api";
import { formatPrice } from "@/utils/constants";

const CartPage = ({ user, userCurrency }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], bundles: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showBundleDialog, setShowBundleDialog] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundleOfferPrice, setBundleOfferPrice] = useState("");
  const [bundleMessage, setBundleMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      const response = await api.get('/cart');
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (listingId, newQuantity, variantId = null) => {
    try {
      await api.put(`/cart/update/${listingId}`, { quantity: newQuantity, variant_id: variantId });
      window.dispatchEvent(new Event('cart:updated'));
      fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quantity');
    }
  };

  const removeItem = async (listingId, variantId = null) => {
    try {
      const url = variantId ? `/cart/remove/${listingId}?variant_id=${encodeURIComponent(variantId)}` : `/cart/remove/${listingId}`;
      await api.delete(url);
      toast.success('Item removed from cart');
      window.dispatchEvent(new Event('cart:updated'));
      fetchCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const openBundleOffer = (bundle) => {
    setSelectedBundle(bundle);
    setBundleOfferPrice(bundle.subtotal.toFixed(2));
    setBundleMessage("");
    setShowBundleDialog(true);
  };

  const submitBundleOffer = async () => {
    if (!selectedBundle || !bundleOfferPrice) return;
    
    const offerPrice = parseFloat(bundleOfferPrice);
    if (offerPrice <= 0 || offerPrice >= selectedBundle.subtotal) {
      toast.error('Please enter a valid offer price less than the total');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/cart/bundle-offer', {
        seller_id: selectedBundle.seller_id,
        listing_ids: selectedBundle.items.map(i => i.listing_id),
        offered_price: offerPrice,
        message: bundleMessage
      });
      toast.success('Bundle offer sent to seller!');
      setShowBundleDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally {
      setSubmitting(false);
    }
  };

  const [checkingOut, setCheckingOut] = useState(null);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [checkoutBundle, setCheckoutBundle] = useState(null);
  // Ship-to fields
  const [shipName, setShipName] = useState(user?.name || "");
  const [shipStreet, setShipStreet] = useState(user?.address?.street || "");
  const [shipApt, setShipApt] = useState("");
  const [shipCity, setShipCity] = useState(user?.city || user?.location?.city || "");
  const [shipState, setShipState] = useState(user?.state || user?.location?.state || "");
  const [shipZip, setShipZip] = useState(user?.address?.zip_code || "");
  const [shipPhone, setShipPhone] = useState(user?.phone || "");

  const openCheckout = (bundle) => {
    setCheckoutBundle(bundle);
    setShowCheckoutDialog(true);
  };

  const handleCheckout = async () => {
    if (!checkoutBundle) return;
    if (!shipName.trim() || !shipStreet.trim() || !shipCity.trim() || !shipState.trim() || !shipZip.trim() || !shipPhone.trim()) {
      toast.error('Please fill all shipping fields including phone.');
      return;
    }
    setCheckingOut(checkoutBundle.seller_id);
    try {
      const res = await api.post(`/cart/checkout/${checkoutBundle.seller_id}`, {
        origin_url: window.location.origin,
        buyer_state: shipState.toUpperCase(),
        buyer_city: shipCity,
        buyer_country: "United States",
        shipping_name: shipName,
        shipping_street: shipStreet,
        shipping_apt: shipApt,
        shipping_city: shipCity,
        shipping_state: shipState.toUpperCase(),
        shipping_zip: shipZip,
        shipping_country: "United States",
        shipping_phone: shipPhone
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-52 pb-16 bg-slate-50 flex items-start justify-center">
        <Card className="max-w-md w-full mx-4 mt-12">
          <CardContent className="pt-6 text-center">
            <ShoppingCart className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Sign in to view your cart</h2>
            <p className="text-muted-foreground mb-4">
              Create an account or sign in to start shopping
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-52 bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-52 pb-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <Card className="text-center py-12">
            <CardContent>
              <ShoppingCart className="h-20 w-20 mx-auto text-slate-300 mb-4" />
              <h2 className="text-2xl font-semibold mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-6">
                Start shopping to add items to your cart
              </p>
              <Button onClick={() => navigate('/')} size="lg">
                Browse Listings
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-52 pb-16 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShoppingCart className="h-6 w-6" />
              My Cart
              <Badge variant="secondary" className="ml-2">
                {cart.item_count} {cart.item_count === 1 ? 'item' : 'items'}
              </Badge>
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items - Grouped by Seller (Bundles) */}
          <div className="lg:col-span-2 space-y-6">
            {cart.bundles.map((bundle) => (
              <Card key={bundle.seller_id} className="overflow-hidden">
                <CardHeader className="bg-slate-50 border-b py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-slate-500" />
                      <span className="font-medium">{bundle.seller_name}'s Items</span>
                      <Badge variant="outline" className="text-xs">
                        {bundle.item_count} {bundle.item_count === 1 ? 'item' : 'items'}
                      </Badge>
                    </div>
                    {bundle.items.length > 1 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openBundleOffer(bundle)}
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      >
                        <Percent className="h-3 w-3 mr-1" />
                        Request Bundle Discount
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="divide-y">
                  {bundle.items.map((item) => (
                    <div key={`${item.listing_id}_${item.variant_id || 'novariant'}`} className="py-4 flex gap-4">
                      {/* Item Image */}
                      <div 
                        className="w-24 h-24 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0 cursor-pointer"
                        onClick={() => navigate(`/item/${item.listing_id}`)}
                      >
                        <img 
                          src={item.images?.[0] || 'https://via.placeholder.com/96'} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="font-medium text-sm line-clamp-2 cursor-pointer hover:text-emerald-600"
                          onClick={() => navigate(`/item/${item.listing_id}`)}
                        >
                          {item.title}
                        </h3>
                        {item.variant && (item.variant.color || item.variant.size) && (
                          <div className="flex flex-wrap gap-1 mt-1" data-testid="cart-item-variant">
                            {item.variant.color && (
                              <Badge variant="outline" className="text-xs bg-slate-50">
                                Color: <span className="ml-1 font-medium">{item.variant.color}</span>
                              </Badge>
                            )}
                            {item.variant.size && (
                              <Badge variant="outline" className="text-xs bg-slate-50">
                                Size: <span className="ml-1 font-medium">{item.variant.size}</span>
                              </Badge>
                            )}
                          </div>
                        )}
                        <p className="text-lg font-bold text-emerald-600 mt-1">
                          {formatPrice(item.price, userCurrency)}
                        </p>
                        {item.condition && (
                          <Badge variant="secondary" className="text-xs mt-1 capitalize">
                            {item.condition}
                          </Badge>
                        )}
                      </div>

                      {/* Quantity & Actions */}
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2 border rounded-lg">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.listing_id, item.quantity - 1, item.variant_id)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.listing_id, item.quantity + 1, item.variant_id)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeItem(item.listing_id, item.variant_id)}
                          data-testid={`remove-cart-item-${item.listing_id}-${item.variant_id || 'none'}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Bundle Footer */}
                  <div className="pt-4 flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Subtotal ({bundle.item_count} items)
                    </div>
                    <div className="text-lg font-bold">
                      {formatPrice(bundle.subtotal, userCurrency)}
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <Button 
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                      onClick={() => openCheckout(bundle)}
                      disabled={checkingOut === bundle.seller_id}
                      data-testid={`checkout-btn-${bundle.seller_id}`}
                    >
                      {checkingOut === bundle.seller_id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Redirecting...
                        </>
                      ) : (
                        <>
                          Checkout from {bundle.seller_name}
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span>Items ({cart.item_count})</span>
                  <span>{formatPrice(cart.total, userCurrency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Shipping</span>
                  <span className="text-muted-foreground">Calculated at checkout</span>
                </div>
                <hr />
                <div className="flex justify-between font-bold text-lg">
                  <span>Estimated Total</span>
                  <span>{formatPrice(cart.total, userCurrency)}</span>
                </div>
                
                <div className="pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground text-center">
                    Checkout with each seller separately for the best shipping rates
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Bundle Offer Dialog */}
      <Dialog open={showBundleDialog} onOpenChange={setShowBundleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-emerald-600" />
              Request Bundle Discount
            </DialogTitle>
          </DialogHeader>
          
          {selectedBundle && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">
                  {selectedBundle.item_count} items from {selectedBundle.seller_name}
                </p>
                <div className="flex justify-between items-center">
                  <span>Original Total:</span>
                  <span className="font-bold">{formatPrice(selectedBundle.subtotal, userCurrency)}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Your Offer Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input 
                    type="number"
                    placeholder="Enter your offer"
                    value={bundleOfferPrice}
                    onChange={(e) => setBundleOfferPrice(e.target.value)}
                    className="pl-7"
                    step="0.01"
                  />
                </div>
                {bundleOfferPrice && parseFloat(bundleOfferPrice) < selectedBundle.subtotal && (
                  <p className="text-xs text-emerald-600 mt-1">
                    {Math.round((1 - parseFloat(bundleOfferPrice) / selectedBundle.subtotal) * 100)}% discount requested
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Message to Seller (Optional)</label>
                <Textarea 
                  placeholder="Hi! I'm interested in buying these items together..."
                  value={bundleMessage}
                  onChange={(e) => setBundleMessage(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBundleDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitBundleOffer}
              disabled={submitting || !bundleOfferPrice}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Shipping Address Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Where should we ship this?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Full name" value={shipName} onChange={(e) => setShipName(e.target.value)} data-testid="cart-ship-name" />
            <Input placeholder="Street address" value={shipStreet} onChange={(e) => setShipStreet(e.target.value)} data-testid="cart-ship-street" />
            <Input placeholder="Apt, suite (optional)" value={shipApt} onChange={(e) => setShipApt(e.target.value)} data-testid="cart-ship-apt" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="City" value={shipCity} onChange={(e) => setShipCity(e.target.value)} data-testid="cart-ship-city" />
              <Input placeholder="State" maxLength={2} value={shipState} onChange={(e) => setShipState(e.target.value.toUpperCase())} className="uppercase" data-testid="cart-ship-state" />
              <Input placeholder="ZIP" value={shipZip} onChange={(e) => setShipZip(e.target.value)} data-testid="cart-ship-zip" />
            </div>
            <Input type="tel" placeholder="Phone (for delivery)" value={shipPhone} onChange={(e) => setShipPhone(e.target.value)} data-testid="cart-ship-phone" />
            <p className="text-xs text-muted-foreground">The seller uses this to print your shipping label.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckoutDialog(false)} disabled={!!checkingOut}>Cancel</Button>
            <Button
              onClick={handleCheckout}
              disabled={!!checkingOut}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="confirm-cart-checkout-btn"
            >
              {checkingOut ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redirecting...</> : 'Continue to Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CartPage;
